import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth, useTheme, useLanguage, API } from '@/App';
import { getTranslation } from '@/translations';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  MessageSquare,
  ArrowLeft,
  BarChart3,
  Clock,
  ThumbsUp,
  TrendingUp,
  Users,
  Smile,
  Meh,
  Frown,
  MessageCircle
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Pie, Line, Bar } from 'react-chartjs-2';

// Warna ikon KPI (string statis supaya tidak ter-purge Tailwind)
const KPI_TONES = {
  indigo: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400',
  blue: 'bg-blue-100 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400',
  purple: 'bg-purple-100 text-purple-600 dark:bg-purple-500/15 dark:text-purple-400',
  emerald: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400',
  amber: 'bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400',
};

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ChatbotAnalyticsPage = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (key) => getTranslation(language, key);

  const [loading, setLoading] = useState(true);
  const [aiAgent, setAiAgent] = useState(null);
  const [analytics, setAnalytics] = useState({
    total_chats: 0,
    total_conversations: 0,
    unique_sessions: 0,
    avg_response_time: 0,
    sentiment: { positive: 0, neutral: 0, negative: 0 },
    daily_trend: [],
    top_queries: []
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const aiAgentRes = await axios.get(`${API}/chatbots/${id}`, {
        headers,
        withCredentials: true
      });
      setAiAgent(aiAgentRes.data);

      const analyticsRes = await axios.get(`${API}/analytics/${id}`, {
        headers,
        withCredentials: true
      });
      
      console.log('Analytics data received:', analyticsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error(language === 'id' ? 'Gagal memuat data analytics' : 'Failed to load analytics data');
      console.error('Analytics fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalSentiment = analytics.sentiment.positive + analytics.sentiment.neutral + analytics.sentiment.negative;
  const satisfactionRate = totalSentiment > 0 
    ? Math.round((analytics.sentiment.positive / totalSentiment) * 100)
    : 0;

  const avgResponseTime = analytics.avg_response_time || 0;

  // Chart.js configurations
  const isDark = theme === 'dark';
  const textColor = isDark ? '#e2e8f0' : '#1e293b';
  const gridColor = isDark ? '#334155' : '#e2e8f0';

  // Sentiment Pie Chart Data
  const sentimentData = {
    labels: [
      language === 'id' ? 'Positif' : 'Positive',
      language === 'id' ? 'Netral' : 'Neutral',
      language === 'id' ? 'Negatif' : 'Negative'
    ],
    datasets: [{
      data: [
        analytics.sentiment.positive,
        analytics.sentiment.neutral,
        analytics.sentiment.negative
      ],
      backgroundColor: ['#10B981', '#6B7280', '#EF4444'],
      borderColor: isDark ? '#1e293b' : '#ffffff',
      borderWidth: 2
    }]
  };

  const sentimentOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          color: textColor,
          padding: 15,
          font: { size: 12 }
        }
      },
      tooltip: {
        backgroundColor: isDark ? '#1e293b' : '#ffffff',
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: gridColor,
        borderWidth: 1
      }
    }
  };

  // Trend Line Chart Data
  const trendData = {
    labels: analytics.daily_trend.map(d => d.date),
    datasets: [{
      label: language === 'id' ? 'Percakapan' : 'Conversations',
      data: analytics.daily_trend.map(d => d.count),
      borderColor: '#10B981',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      tension: 0.4,
      fill: true,
      pointRadius: 4,
      pointHoverRadius: 6
    }]
  };

  const trendOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: isDark ? '#1e293b' : '#ffffff',
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: gridColor,
        borderWidth: 1
      }
    },
    scales: {
      x: {
        grid: { color: gridColor },
        ticks: { color: textColor }
      },
      y: {
        grid: { color: gridColor },
        ticks: { color: textColor, precision: 0 },
        beginAtZero: true
      }
    }
  };

  // Word Frequency Bar Chart Data (Horizontal)
  const wordData = {
    labels: analytics.top_queries.map(q => q.word),
    datasets: [{
      label: language === 'id' ? 'Jumlah' : 'Count',
      data: analytics.top_queries.map(q => q.count),
      backgroundColor: '#10B981',
      borderColor: '#059669',
      borderWidth: 1
    }]
  };

  const wordOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: isDark ? '#1e293b' : '#ffffff',
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: gridColor,
        borderWidth: 1
      }
    },
    scales: {
      x: {
        grid: { color: gridColor },
        ticks: { color: textColor, precision: 0 },
        beginAtZero: true
      },
      y: {
        grid: { display: false },
        ticks: { color: textColor }
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A]">
      {/* Header */}
      <header className="sticky top-0 backdrop-blur-xl bg-white/85 dark:bg-[#0A0A0A]/85 border-b border-slate-200 dark:border-zinc-800/80 z-20">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-4 min-w-0">
            {/* <Link to="/analytics">
              <Button variant="ghost" size="sm" className="dark:text-zinc-200 dark:hover:bg-zinc-800">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link> */}
            <div className="flex items-center gap-2.5 min-w-0">
              <div 
                className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: (aiAgent?.primary_color || '#10B981') + '20' }}
              >
                <BarChart3 className="w-4 h-4" style={{ color: aiAgent?.primary_color || '#10B981' }} />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 truncate">
                  {aiAgent?.name}
                </h1>
                <p className="text-[11px] sm:text-xs text-slate-500 dark:text-zinc-400 truncate">
                  {language === 'id' ? 'Analitik AI Agent' : 'AI Agent Analytics'}
                </p>
              </div>
            </div>
          </div>
          <Link to={`/agents`} className="shrink-0">
            <Button variant="outline" size="sm" className="h-8 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800" data-testid="analytics-back-btn">
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
              {language === 'id' ? 'Kembali' : 'Back'}
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* KPI — compact strip (2 kolom di mobile, 5 di desktop) */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3 mb-5 sm:mb-8" data-testid="analytics-kpi-grid">
          {[
            {
              id: 'conversations', icon: Users, tone: 'indigo', testId: 'total-conversations',
              label: language === 'id' ? 'Percakapan' : 'Conversations',
              value: (analytics.total_conversations || 0).toLocaleString(),
              sub: `${analytics.unique_sessions} ${language === 'id' ? 'sesi unik' : 'unique sessions'}`,
            },
            {
              id: 'messages', icon: MessageCircle, tone: 'blue', testId: 'total-messages',
              label: language === 'id' ? 'Pesan' : 'Messages',
              value: (analytics.total_chats || 0).toLocaleString(),
              sub: language === 'id' ? 'dari customer' : 'from customers',
            },
            {
              id: 'response', icon: Clock, tone: 'purple', testId: 'avg-response-time',
              label: language === 'id' ? 'Respons' : 'Response',
              value: avgResponseTime > 0 ? `${avgResponseTime}s` : '-',
              sub: language === 'id' ? 'rata-rata' : 'average',
            },
            {
              id: 'satisfaction', icon: ThumbsUp, tone: 'emerald', testId: 'satisfaction-rate',
              label: language === 'id' ? 'Kepuasan' : 'Satisfaction',
              value: `${satisfactionRate}%`,
              sub: `${analytics.sentiment.positive} ${language === 'id' ? 'positif' : 'positive'}`,
            },
            {
              id: 'active-users', icon: Users, tone: 'amber', testId: 'active-users',
              label: language === 'id' ? 'Sesi Aktif' : 'Active Sessions',
              value: analytics.unique_sessions,
              sub: language === 'id' ? 'sesi unik' : 'unique sessions',
            },
          ].map(({ id, icon: Icon, tone, label, value, sub, testId }) => (
            <div
              key={id}
              className={`bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800/80 p-3 sm:p-4 transition-colors hover:border-slate-300 dark:hover:border-zinc-700 ${id === 'active-users' ? 'col-span-2 lg:col-span-1' : ''}`}
            >
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${KPI_TONES[tone]}`}>
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <span className="text-[10px] sm:text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-zinc-400 truncate">
                  {label}
                </span>
              </div>
              <p
                className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50 leading-none"
                data-testid={testId}
              >
                {value}
              </p>
              <p className="text-[10px] sm:text-xs text-slate-400 dark:text-zinc-500 mt-1 truncate">{sub}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Sentiment Analysis */}
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
            <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-4 flex items-center gap-2">
              <Smile className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              {language === 'id' ? 'Analisis Sentimen' : 'Sentiment Analysis'}
            </h2>
            
            {totalSentiment === 0 ? (
              <div className="text-center py-12 text-slate-500 dark:text-zinc-400">
                <Meh className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{language === 'id' ? 'Belum ada data sentimen' : 'No sentiment data yet'}</p>
              </div>
            ) : (
              <>
                <div style={{ height: '250px' }}>
                  <Pie data={sentimentData} options={sentimentOptions} />
                </div>
                
                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <Smile className="w-5 h-5 text-green-600 mx-auto mb-1 dark:text-green-400" />
                    <p className="text-sm font-semibold text-slate-900 dark:text-zinc-50">
                      {analytics.sentiment.positive}
                    </p>
                    <p className="text-xs text-slate-600 dark:text-zinc-400">
                      {language === 'id' ? 'Positif' : 'Positive'}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 dark:bg-[#0A0A0A] rounded-lg">
                    <Meh className="w-5 h-5 text-slate-600 mx-auto mb-1" />
                    <p className="text-sm font-semibold text-slate-900 dark:text-zinc-50">
                      {analytics.sentiment.neutral}
                    </p>
                    <p className="text-xs text-slate-600 dark:text-zinc-400">
                      {language === 'id' ? 'Netral' : 'Neutral'}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <Frown className="w-5 h-5 text-red-600 mx-auto mb-1 dark:text-red-400" />
                    <p className="text-sm font-semibold text-slate-900 dark:text-zinc-50">
                      {analytics.sentiment.negative}
                    </p>
                    <p className="text-xs text-slate-600 dark:text-zinc-400">
                      {language === 'id' ? 'Negatif' : 'Negative'}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Trend Chart */}
          <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
            <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              {language === 'id' ? 'Tren Percakapan' : 'Conversation Trend'}
            </h2>
            
            {analytics.daily_trend.length === 0 ? (
              <div className="text-center py-12 text-slate-500 dark:text-zinc-400">
                <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{language === 'id' ? 'Belum ada data tren' : 'No trend data yet'}</p>
              </div>
            ) : (
              <div style={{ height: '250px' }}>
                <Line data={trendData} options={trendOptions} />
              </div>
            )}
          </div>
        </div>

        {/* Word Frequency */}
        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            {language === 'id' ? 'Kata-kata Paling Sering Muncul' : 'Most Frequent Words'}
          </h2>
          
          {analytics.top_queries.length === 0 ? (
            <div className="text-center py-12 text-slate-500 dark:text-zinc-400">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{language === 'id' ? 'Belum ada data kata' : 'No word data yet'}</p>
            </div>
          ) : (
            <div style={{ height: '300px' }}>
              <Bar data={wordData} options={wordOptions} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ChatbotAnalyticsPage;
